// CampusMap — the fast-travel minimap. A small floating map-pin button
// anchored top-right of the HUD area opens a 2D top-down illustration of
// the campus with clickable "Teleport" hotspots for the Quad plaza, the
// West-Wing Baseball diamond, and the South-East Sports Arena entrance.
//
// The map itself is a small SVG so it stays crisp at any DPI. Coordinates
// are hand-picked to match the world layout (entrance plaza at [40,0,40],
// baseball at [-30,0,40], basketball at [40,0,15], football at [40,0,70],
// soccer at [70,0,40]). Clicking a marker publishes the same
// `window.__zone_walker_teleport(x,y,z)` call the ArenaHUD "Go to Arena"
// button uses, and — for touch visitors — flips into walk mode so the
// on-screen joystick is up on arrival.

import React, { useState } from "react";
import { Map as MapIcon, X, MapPin } from "lucide-react";

import { COLORS } from "../constants";

const DESTINATIONS = [
  { id: "quad",       label: "Campus Quad",  x: 0,   z: 0,   worldX: 0,   worldZ: 0,   sport: null },
  { id: "arena",      label: "Sports Arena", x: 55,  z: 45,  worldX: 40,  worldZ: 40,  sport: null },
  { id: "basketball", label: "Basketball",   x: 55,  z: 20,  worldX: 45,  worldZ: 20,  sport: "basketball" },
  { id: "football",   label: "Football",     x: 55,  z: 82,  worldX: 40,  worldZ: 70,  sport: "football" },
  { id: "soccer",     label: "Soccer",       x: 82,  z: 45,  worldX: 70,  worldZ: 40,  sport: "soccer" },
  { id: "baseball",   label: "Baseball",     x: 15,  z: 45,  worldX: -45, worldZ: 55,  sport: "baseball" },
];

export default function CampusMap({ arenaApi, isTouch }) {
  const [open, setOpen] = useState(false);

  // Bottom-left stack: Map sits ON TOP of the HowToPlay pill. HowToPlay's
  // bottom is 138 (desktop) / 190 (touch); stack the Map ~46px higher so
  // the two pills read as a clean vertical group.
  const bottomOffset = isTouch ? 236 : 184;

  const teleport = (dest) => {
    // Switch the active sport in the arenaApi first (so the ball, click
    // plane, crowd etc. re-anchor before the camera settles).
    if (dest.sport && typeof arenaApi.setSport === "function") {
      arenaApi.setSport(dest.sport);
    }
    // Use arenaApi.teleportRef for the arena / sport destinations, so we
    // land at the entrance plaza rather than a bare [x,y,z] snap. Sport
    // destinations still teleport to the entrance plaza — the entrance
    // is the arrival landmark; the visitor then walks / uses HUD sport
    // switch. For the Campus Quad destination, use a direct walker
    // teleport to the world origin.
    if (dest.id === "quad") {
      try {
        if (typeof window.__zone_walker_teleport === "function") {
          window.__zone_walker_teleport(0, 0, 0);
        }
      } catch (e) {
        /* ignore */
      }
    } else {
      const t = arenaApi?.teleportRef?.current?.teleport;
      if (typeof t === "function") t();
    }
    if (isTouch) {
      try {
        if (typeof window.__zone_enter_walk === "function") {
          window.__zone_enter_walk();
        }
      } catch (e) {
        /* ignore */
      }
    }
    setOpen(false);
  };

  return (
    <>
      {/* Floating minimap trigger — bottom-left, sits ABOVE the HowToPlay
          pill in the utility stack. Rendered on both desktop and touch. */}
      <button
        data-testid="campus-map-btn"
        onClick={() => setOpen(true)}
        aria-label="Open Campus Map"
        style={{
          position: "fixed",
          left: 20,
          bottom: bottomOffset,
          // z-index kept below the modal overlays (91/92) so any expanded
          // modal (Sports Arena widget, PlayNow, ArenaHUD panel) cleanly
          // sits above the utility buttons.
          zIndex: 40,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          borderRadius: 20,
          background: "linear-gradient(180deg, rgba(11,21,48,0.85) 0%, rgba(6,14,32,0.85) 100%)",
          border: `1px solid ${COLORS.gold}`,
          color: "#f7dc95",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          cursor: "pointer",
          boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
          backdropFilter: "blur(6px)",
        }}
      >
        <MapIcon size={13} />
        Campus Map
      </button>

      {open && (
        <div
          data-testid="campus-map-overlay"
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 91,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(11,21,48,0.55)",
            backdropFilter: "blur(4px)",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: 520,
              width: "100%",
              padding: "24px 24px 20px",
              borderRadius: 18,
              background:
                "linear-gradient(180deg, rgba(11,21,48,0.96) 0%, rgba(6,14,32,0.96) 100%)",
              border: `1px solid ${COLORS.gold}`,
              boxShadow: "0 24px 56px rgba(0,0,0,0.5), 0 0 0 6px rgba(230,180,80,0.08)",
              color: "#f3ead4",
            }}
          >
            <button
              data-testid="campus-map-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                padding: 6,
                background: "transparent",
                border: "none",
                color: "#c9b382",
                cursor: "pointer",
                opacity: 0.8,
              }}
            >
              <X size={18} />
            </button>

            <div
              style={{
                fontSize: 11,
                letterSpacing: 2,
                color: "#c9b382",
                fontWeight: 700,
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              Fast Travel
            </div>
            <h2
              style={{
                margin: 0,
                fontFamily: "'Cormorant Garamond','Playfair Display',serif",
                fontSize: 24,
                fontWeight: 600,
                color: "#f7dc95",
              }}
            >
              Campus Map
            </h2>

            {/* SVG map */}
            <svg
              viewBox="0 0 100 100"
              style={{
                display: "block",
                width: "100%",
                marginTop: 16,
                borderRadius: 12,
                background: "linear-gradient(160deg, #0d1a3a 0%, #050b21 100%)",
                border: "1px solid rgba(230,180,80,0.2)",
              }}
            >
              {/* Cardinal labels */}
              <text x="50" y="4" textAnchor="middle" fill="#c9b382" fontSize="4">N</text>
              <text x="50" y="98" textAnchor="middle" fill="#c9b382" fontSize="4">S</text>
              <text x="2" y="52" textAnchor="middle" fill="#c9b382" fontSize="4">W</text>
              <text x="98" y="52" textAnchor="middle" fill="#c9b382" fontSize="4">E</text>
              {/* Central Quad ring */}
              <circle cx="30" cy="45" r="10" fill="none" stroke="#e6b450" strokeWidth="0.4" strokeDasharray="1 1" opacity="0.7" />
              {/* Path lines from Quad → arena entrance → each sport */}
              <line x1="30" y1="45" x2="55" y2="45" stroke="#e6b450" strokeWidth="0.35" opacity="0.55" />
              <line x1="55" y1="45" x2="55" y2="20" stroke="#e6b450" strokeWidth="0.35" opacity="0.55" strokeDasharray="1 1.2" />
              <line x1="55" y1="45" x2="55" y2="82" stroke="#e6b450" strokeWidth="0.35" opacity="0.55" strokeDasharray="1 1.2" />
              <line x1="55" y1="45" x2="82" y2="45" stroke="#e6b450" strokeWidth="0.35" opacity="0.55" strokeDasharray="1 1.2" />
              <line x1="30" y1="45" x2="15" y2="45" stroke="#e6b450" strokeWidth="0.35" opacity="0.55" strokeDasharray="1 1.2" />

              {/* Destination markers */}
              {DESTINATIONS.map((d) => {
                const isQuad = d.id === "quad";
                const cx = isQuad ? 30 : d.x;
                const cy = isQuad ? 45 : d.z;
                const color =
                  d.id === "basketball" ? "#f26a34"
                  : d.id === "football" ? "#7d4dd6"
                  : d.id === "soccer" ? "#4bb352"
                  : d.id === "baseball" ? "#c8353a"
                  : d.id === "arena" ? "#e6b450"
                  : "#f7dc95";
                return (
                  <g key={d.id} style={{ cursor: "pointer" }}>
                    <circle
                      cx={cx}
                      cy={cy}
                      r={isQuad ? 3.2 : 2.4}
                      fill={color}
                      stroke="#f7dc95"
                      strokeWidth="0.35"
                      onClick={() => teleport(d)}
                      data-testid={`map-pin-${d.id}`}
                    />
                    <text
                      x={cx}
                      y={cy + 6.5}
                      textAnchor="middle"
                      fill="#f3ead4"
                      fontSize="2.8"
                      style={{ pointerEvents: "none", fontFamily: "sans-serif" }}
                    >
                      {d.label.toUpperCase()}
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* Quick teleport buttons for the three primary destinations */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 8,
                marginTop: 14,
              }}
            >
              <TelBtn testid="map-tp-quad" onClick={() => teleport(DESTINATIONS[0])}>
                Quad
              </TelBtn>
              <TelBtn testid="map-tp-arena" onClick={() => teleport(DESTINATIONS[1])}>
                Sports Arena
              </TelBtn>
              <TelBtn testid="map-tp-baseball" onClick={() => teleport(DESTINATIONS[5])}>
                Baseball
              </TelBtn>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TelBtn({ testid, onClick, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "9px 10px",
        borderRadius: 10,
        border: "1px solid rgba(230,180,80,0.4)",
        background: "linear-gradient(180deg, rgba(11,21,48,0.85) 0%, rgba(6,14,32,0.85) 100%)",
        color: "#f7dc95",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1.4,
        textTransform: "uppercase",
        cursor: "pointer",
      }}
    >
      <MapPin size={11} />
      {children}
    </button>
  );
}
